/**
 * The app's top bar (PM efb0e798), for staff and families alike,
 * Accelimate-style:
 *
 *   left   menu button (phone) · the club's logo, or its name when it has
 *          no logo · the control that collapses the side navigation (desktop)
 *   right  call status (staff who take calls; desktop) · search (Ctrl+K)
 *          · help · Display (light/dark, text size) · notifications · the
 *          account menu (Club Central platform tools are in it, for
 *          platform staff only)
 *
 * Neutral, not club-colored (PM 5b6978ff, Dan 2026-10-31): the club's
 * identity is its logo plus one accent color, used on primary actions and
 * the active page, so the frame stays white (gray in dark mode).
 * There is no club switcher: a person signs in to one club; only Club
 * Central platform staff switch, and they do it from the CC tools pill.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, HelpCircle, Menu } from "lucide-react";
import NotificationBell from "../common/NotificationBell";
import TopBarSearch from "./TopBarSearch";
import DisplaySettingsDrawer from "./DisplaySettingsDrawer";
import type { SearchItem } from "../../search/searchTypes";
import type { ServerSearchType } from "../../api/searchAPI";

interface TopBarProps {
  homeHref: string;
  logoUrl?: string | null;
  businessName: string;
  isMobileDevice: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onOpenDrawer: () => void;
  /** null hides search (families have none, as before) */
  search: { pages: SearchItem[]; serverTypes: ServerSearchType[] } | null;
  /** the help surface this person can open; null hides the button */
  helpHref: string | null;
  isDarkMode: boolean;
  /** call routing status (desktop; the drawer holds it on a phone) */
  callStatus?: React.ReactNode;
  accountMenu: React.ReactNode;
}

const TopBar: React.FC<TopBarProps> = ({
  homeHref,
  logoUrl,
  businessName,
  isMobileDevice,
  collapsed,
  onToggleCollapse,
  onOpenDrawer,
  search,
  helpHref,
  isDarkMode,
  callStatus,
  accountMenu,
}) => {
  const iconBtn = `p-2 rounded-full transition-colors ${
    isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"
  }`;
  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 h-14 flex items-center gap-1 sm:gap-2 px-2 sm:px-3 border-b shadow-sm ${
        isDarkMode
          ? "bg-gray-900 text-gray-100 border-gray-700"
          : "bg-white text-gray-800 border-gray-200"
      }`}
      data-testid="top-bar"
    >
      <button
        type="button"
        className={`${isMobileDevice ? "block" : "hidden"} ${iconBtn} flex-shrink-0`}
        onClick={onOpenDrawer}
        aria-label="Open sidebar"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* The logo IS the club's name; the name shows only when there is no logo. */}
      <Link
        to={homeHref}
        className="flex items-center min-w-0"
        title={businessName}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={businessName}
            className="h-8 sm:h-9 w-auto max-w-[9rem] object-contain flex-shrink-0"
          />
        ) : (
          <span className="font-semibold text-base sm:text-lg truncate">
            {businessName}
          </span>
        )}
      </Link>

      {!isMobileDevice && (
        <button
          type="button"
          className={`${iconBtn} flex-shrink-0`}
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Show the page list" : "Hide the page list"}
        >
          <ChevronLeft
            className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`}
          />
        </button>
      )}

      <div className="ml-auto flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0">
        {!isMobileDevice && callStatus}
        {search && (
          <TopBarSearch
            pages={search.pages}
            serverTypes={search.serverTypes}
            compact={isMobileDevice}
            isDarkMode={isDarkMode}
          />
        )}
        {helpHref && (
          <Link
            to={helpHref}
            className={`${iconBtn} hidden sm:block`}
            aria-label="Help"
            title="Help"
          >
            <HelpCircle className="w-5 h-5" />
          </Link>
        )}
        {/* Text size and light/dark, on every screen size. */}
        <DisplaySettingsDrawer triggerClassName={iconBtn} />
        {/* Members get the bell too: notifications are keyed on user_id and
          the list endpoint scopes to the caller's own session. */}
        <NotificationBell align="right" />
        {accountMenu}
      </div>
    </header>
  );
};

export default TopBar;
